"use client";
import React, { useEffect, useState } from "react";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import Icon from "@/components/icons";
import type { IconTypes } from "@/components/icons/types";

type Props = {
  title: string;
  rootClassName?: string;
  itemClassName?: string;
  headerClassName?: string;
  triggerClassName?: string;
  iconClassName?: string;
  contentClassName?: string;
  childrenClassName?: string;
  children?: React.ReactNode;
  expandByDefault?: boolean;
  titleIcon?: IconTypes;
};
export default function AccordionContent({
  title,
  rootClassName,
  itemClassName,
  headerClassName,
  triggerClassName,
  iconClassName,
  contentClassName,
  childrenClassName = "py-4",
  children,
  expandByDefault,
  titleIcon,
}: Props) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openItem, setOpenItem] = useState(expandByDefault ? title : "");

  const hashToOpen: { [key: string]: string } = {
    "#whygetsmarter": "Why GetSmarter?",
    "#brochure": "Request more info",
  };

  const handleHashChange = () => {
    const hash = hashToOpen[window.location.hash] || "";
    if (hash) {
      setOpenItem(hash);
    }
  };

  // On client-side hydration, initialize Radix UI behavior
  useEffect(() => {
    setIsHydrated(true);
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // For SSR/SEO: show content by default using native HTML details/summary
  // After hydration: use Radix UI behavior
  if (!isHydrated) {
    return (
      <div className={rootClassName}>
        <details open className={itemClassName}>
          <summary
            className={cn(
              "bg-[#fbfaf9] md:bg-putty-200 flex flex-1 py-2 px-5 mt-2 items-center justify-between text-lg text-left font-bold cursor-pointer list-none [&::-webkit-details-marker]:hidden",
              triggerClassName,
            )}
          >
            <span className="flex items-center gap-2">
              {titleIcon && <Icon type={titleIcon} />}
              {title}
            </span>
            <ChevronDownIcon
              className={cn(
                "h-5 w-5 shrink-0 text-brand font-bold transition-transform duration-200",
                iconClassName,
              )}
            />
          </summary>
          <div className={cn("overflow-hidden text-sm", contentClassName)}>
            <div className={childrenClassName}>{children}</div>
          </div>
        </details>
      </div>
    );
  }

  // After hydration: use Radix UI for rich interactions
  return (
    <Accordion.Root
      type="single"
      collapsible
      className={rootClassName}
      value={openItem}
      onValueChange={setOpenItem}
    >
      <Accordion.Item value={title} className={itemClassName}>
        <Accordion.Header className={cn("flex m-0", headerClassName)}>
          <Accordion.Trigger
            className={cn(
              "bg-[#fbfaf9] md:bg-putty-200 flex flex-1 py-2 px-5 mt-2 items-center justify-between text-lg text-left font-bold transition-all [&[data-state=open]>svg]:rotate-180",
              triggerClassName,
            )}
          >
            <span className="flex items-center gap-2">
              {titleIcon && <Icon type={titleIcon} />}
              {title}
            </span>
            <ChevronDownIcon
              className={cn(
                "h-5 w-5 shrink-0 text-brand font-bold transition-transform duration-200",
                iconClassName,
              )}
            />
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content
          className={cn(
            "overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
            contentClassName,
          )}
        >
          <div className={childrenClassName}>{children}</div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}
